
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Label } from "@/components/ui/label";
import { AlertCircle, Check } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmedPassword, setConfirmedPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isResetFlow, setIsResetFlow] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Gère à la fois les paramètres d'URL et les fragments d'URL (#)
    const searchParamsType = searchParams.get("type");
    
    // Extraction des paramètres du fragment d'URL (après le #)
    const hashParams = new URLSearchParams(location.hash.replace(/^#\/[^?]*\??/, ''));
    const hashType = hashParams.get("type");
    
    const type = searchParamsType || hashType;
    
    console.log("URL type:", type);
    console.log("Current location:", location);
    console.log("SearchParams:", Object.fromEntries(searchParams.entries()));
    console.log("Hash fragment:", location.hash);
    console.log("User logged in:", !!user);
    
    // Si nous détectons un flux de réinitialisation, déconnectons l'utilisateur actuel
    const handleRecoveryFlow = async () => {
      if (type === "recovery") {
        setIsResetFlow(true);
        
        // Déconnecte l'utilisateur pour éviter les conflits de session
        if (user) {
          console.log("Déconnexion de l'utilisateur pour le flux de réinitialisation");
          await supabase.auth.signOut();
        }
      } else {
        setErrorMessage("Lien invalide ou expiré. Veuillez demander un nouveau lien de réinitialisation.");
      }
    };
    
    handleRecoveryFlow();
  }, [searchParams, location, user]);

  // N'effectue pas de redirection automatique si l'utilisateur est dans un flux de réinitialisation
  // même s'il est déjà connecté
  useEffect(() => {
    document.title = "Réinitialisation du mot de passe | AlunaTools";
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password.length < 6) {
      setErrorMessage("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    if (password !== confirmedPassword) {
      setErrorMessage("Les mots de passe ne correspondent pas");
      return;
    }
    
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      
      setConfirmed(true);
      toast({
        title: "Mot de passe mis à jour",
        description: "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
        variant: "default",
      });
      
      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate("/auth");
      }, 3000);
    } catch (error: any) {
      setErrorMessage(error.message || "Une erreur est survenue lors de la mise à jour du mot de passe");
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la mise à jour du mot de passe",
        variant: "destructive",
      });
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex bg-gray-50 w-full">
        <div className="w-full flex flex-col items-center justify-center">
          <div className="w-full max-w-md">
            <div className="mb-8 text-center">
              <img 
                src="/lovable-uploads/e2b3cf9e-19de-4b54-9306-7b53664ced03.png" 
                alt="AlunaTools Logo" 
                className="h-16 w-auto mx-auto mb-4"
              />
            </div>
            
            <Card className="w-full">
              <CardContent className="p-6">
                {confirmed ? (
                  <div className="text-center space-y-4">
                    <div className="mx-auto bg-green-100 p-3 rounded-full w-16 h-16 flex items-center justify-center">
                      <Check className="h-8 w-8 text-green-600" />
                    </div>
                    <h1 className="text-xl font-semibold">Mot de passe mis à jour avec succès !</h1>
                    <p className="text-gray-600">Vous allez être redirigé vers la page de connexion...</p>
                  </div>
                ) : !isResetFlow ? (
                  <div className="text-center space-y-4">
                    <div className="mx-auto bg-red-100 p-3 rounded-full w-16 h-16 flex items-center justify-center">
                      <AlertCircle className="h-8 w-8 text-red-600" />
                    </div>
                    <h1 className="text-xl font-semibold">Lien invalide ou expiré</h1>
                    <p className="text-gray-600">{errorMessage}</p>
                    <Button 
                      onClick={() => navigate("/auth?tab=forgot-password")} 
                      className="mt-4"
                    >
                      Demander un nouveau lien
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="text-center mb-2">
                      <h1 className="text-xl font-semibold">Définir un nouveau mot de passe</h1>
                      <p className="text-gray-600 text-sm mt-1">
                        Choisissez un mot de passe sécurisé pour votre compte
                      </p>
                    </div>
                    
                    {errorMessage && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
                        <AlertCircle className="h-5 w-5" />
                        <span>{errorMessage}</span>
                      </div>
                    )}
                    
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="password">Nouveau mot de passe</Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <p className="text-xs text-gray-500">
                          Minimum 6 caractères
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="confirmedPassword">Confirmer le mot de passe</Label>
                        <Input
                          id="confirmedPassword"
                          type="password"
                          placeholder="••••••••"
                          value={confirmedPassword}
                          onChange={(e) => setConfirmedPassword(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                    
                    <Button type="submit" className="w-full">
                      Mettre à jour le mot de passe
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
            
            <div className="mt-6 text-center">
              <Button 
                variant="link" 
                onClick={() => navigate("/auth")}
                className="text-gray-600 hover:text-gray-900"
              >
                Retour à la page de connexion
              </Button>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default ResetPassword;
